import type { ReactNode } from "react";
import { CaretLeftIcon, CaretRightIcon } from "@phosphor-icons/react";
import { useI18N } from "common/hooks";
import { cn } from "@/lib/utils";
import { paginationWindow } from "./paginationWindow";

export type PageLinkProps = {
  className: string;
  "aria-current"?: "page";
  "aria-label"?: string;
  rel?: "prev" | "next";
};

/** Renders a real link to `page`, so crawlers and middle-click work; the caller owns routing. */
export type PageLinkRenderer = (page: number, children: ReactNode, props: PageLinkProps) => ReactNode;

type NumberedPaginationProps = {
  currentPage: number;
  totalPages: number;
  renderPageLink: PageLinkRenderer;
};

const itemClass =
  "inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-1 rounded-lg px-3 label-m-semibold focus-blue transition-colors";
const idleClass =
  "border border-border-token-brand-minimal bg-bg-brand-secondary-default text-fg-neutral-strong hover:bg-bg-brand-secondary-hover active:bg-bg-brand-secondary-active";
const currentClass = "bg-bg-brand-primary-default text-fg-neutral-onFilled";

export function NumberedPagination({ currentPage, totalPages, renderPageLink }: NumberedPaginationProps) {
  const i18n = useI18N();
  if (totalPages <= 1) {
    return null;
  }

  return (
    <nav aria-label={i18n.get("placesPaginationLabel")}>
      <ul className="flex flex-wrap items-center gap-2">
        {currentPage > 1 && (
          <li>
            {renderPageLink(
              currentPage - 1,
              <>
                <CaretLeftIcon size={16} aria-hidden="true" focusable="false" />
                <span>{i18n.get("paginationPrevious")}</span>
              </>,
              { className: cn(itemClass, idleClass), rel: "prev" },
            )}
          </li>
        )}
        {paginationWindow(currentPage, totalPages).map((page, index) =>
          page === null ? (
            <li key={`gap-${index}`} aria-hidden="true" className="px-1 label-m-semibold text-fg-neutral-subtle">
              …
            </li>
          ) : (
            <li key={page}>
              {renderPageLink(page, page, {
                className: cn(itemClass, page === currentPage ? currentClass : idleClass),
                "aria-current": page === currentPage ? "page" : undefined,
                "aria-label": i18n.t("placesPageSuffix", { page }),
              })}
            </li>
          ),
        )}
        {currentPage < totalPages && (
          <li>
            {renderPageLink(
              currentPage + 1,
              <>
                <span>{i18n.get("paginationNext")}</span>
                <CaretRightIcon size={16} aria-hidden="true" focusable="false" />
              </>,
              { className: cn(itemClass, idleClass), rel: "next" },
            )}
          </li>
        )}
      </ul>
    </nav>
  );
}

export default NumberedPagination;
